import React from 'react';
import Modal from '../../../components/ui/Modal/Modal';
import Button from '../../../components/ui/Button/Button';
import { FileReportIcon } from '../../../components/ui/Icons';
import type { ActiveFilter } from '../loadsFilters';
import type { ReportKind } from '../useLoadsData';
import styles from './ReportModal.module.css';

/**
 * El paso antes de descargar un reporte (PENDIENTES_TERRESTRE, punto 8; 13-oct-2026).
 *
 * > *"Modal antes de la descarga donde muestre los filtros activos actuales, junto a una nueva opción de 'Reporte
 * > incidencias'"*
 *
 * 🔑 Los dos reportes salen de **las mismas cargas**: las que la tabla muestra con estos filtros. Por eso la lista de
 * filtros va arriba y una sola vez, y debajo se elige cuál de los dos bajar.
 */
interface ReportModalProps {
  open: boolean;
  onClose: () => void;
  /** Lo que va a viajar, ya en palabras (`describeActiveFilters`). */
  filters: ActiveFilter[];
  /** Cuántas cargas cumplen los filtros, según el servidor. */
  total: number;
  generating: ReportKind | null;
  onGenerate: (kind: ReportKind) => Promise<true | string>;
}

const REPORT_OPTIONS: Array<{ kind: ReportKind; title: string; description: string }> = [
  {
    kind: 'loads',
    title: 'Loads report',
    description: 'One row per load, with its details and public link.',
  },
  {
    kind: 'incidents',
    title: 'Incidents report',
    description:
      'Every Protect Time change of these loads: the reason, the carrier, the load and who made the change.',
  },
];

const ReportModal: React.FC<ReportModalProps> = ({ open, onClose, filters, total, generating, onGenerate }) => {
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (open) setError(null);
  }, [open]);

  const generate = async (kind: ReportKind) => {
    setError(null);
    const result = await onGenerate(kind);
    if (result === true) onClose();
    else setError(result);
  };

  return (
    <Modal
      open={open}
      title="Generate report"
      onClose={onClose}
      footer={
        <Button variant="ghost" onClick={onClose}>
          Close
        </Button>
      }
    >
      <div className={styles.body}>
        <section aria-labelledby="report-filters-title">
          <h4 id="report-filters-title" className={styles.sectionTitle}>
            Active filters
          </h4>
          {filters.length === 0 ? (
            <p className={styles.muted}>No filters applied: the reports include every load you have access to.</p>
          ) : (
            <dl className={styles.filters}>
              {filters.map((f) => (
                <div key={f.label} className={styles.filterRow}>
                  <dt className={styles.filterLabel}>{f.label}</dt>
                  <dd className={styles.filterValues}>
                    {f.values.map((v) => (
                      <span key={v} className={styles.chip}>
                        {v}
                      </span>
                    ))}
                  </dd>
                </div>
              ))}
            </dl>
          )}
          <p className={styles.total}>
            {total.toLocaleString()} {total === 1 ? 'load matches' : 'loads match'} these filters.
          </p>
        </section>

        <div className={styles.options}>
          {REPORT_OPTIONS.map((option) => (
            <div key={option.kind} className={styles.option}>
              <div className={styles.optionText}>
                <span className={styles.optionTitle}>{option.title}</span>
                <span className={styles.muted}>{option.description}</span>
              </div>
              <Button
                variant="primary"
                className={styles.optionBtn}
                loading={generating === option.kind}
                disabled={generating !== null}
                onClick={() => generate(option.kind)}
                aria-label={`Download ${option.title}`}
              >
                <FileReportIcon size={15} />
                Download
              </Button>
            </div>
          ))}
        </div>

        {error && (
          <p className={styles.error} role="alert">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
};

export default ReportModal;
